import React, { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import { getUserId } from '../../helpers/auth'
import * as Yup from 'yup'
import { toast } from 'react-toastify'
import { Button, DatePicker, Input, Modal, Select, Upload } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import Loader from '../../pages/pages/loader'
import dayjs from 'dayjs'
import TextArea from 'antd/es/input/TextArea'
import {
  useCreateIptvRecordMutation,
  useUpdateIptvMutation
} from '../../redux/apis/linkManagement/iptv/index/lmIptvApiSlice'
import {
  useCreateIptvMediaFileMutation,
  useGetIptvMediaFilesListQuery
} from '../../redux/apis/linkManagement/iptv/media/lmIptvFilesApiSlice'

const EditCreateIptv = ({ state, open, setOpen, record }) => {
  const [pendingFiles, setPendingFiles] = useState([])
  const [pendingScreenshots, setPendingScreenshots] = useState([])
  const [pendingAppLogo, setPendingAppLogo] = useState(null)
  const [appLogoFileList, setAppLogoFileList] = useState([])
  const [uploadingMedia, setUploadingMedia] = useState(false)

  const { data: existingMediaFiles, refetch: refetchMediaFiles } = useGetIptvMediaFilesListQuery(
    { iptv: record?.id },
    { skip: !record?.id || state !== 'edit' }
  )

  const [
    updateService,
    { isLoading: isUpdating, isSuccess: SuccessUpdate, error: errorUpdate }
  ] = useUpdateIptvMutation()
  const [
    addService,
    { isLoading: isAdding, isSuccess: SuccessAdd, error: errorAdd }
  ] = useCreateIptvRecordMutation()
  const [createIptvMediaFile] = useCreateIptvMediaFileMutation()

  // Validate file types - reject .exe and .sh files
  const validateFile = (file) => {
    const forbiddenExtensions = ['.exe', '.sh', '.bat', '.cmd', '.com', '.msi']
    const fileName = file.name.toLowerCase()
    const isInvalid = forbiddenExtensions.some(ext => fileName.endsWith(ext))

    if (isInvalid) {
      toast.error(`Cannot upload ${file.name}. Executable files are not allowed.`)
      return false
    }
    return true
  }

  const validateScreenshot = (file) => {
    const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp']

    if (!allowedImageTypes.includes(file.type)) {
      toast.error(`Cannot upload ${file.name}. Only image files are allowed for screenshots.`)
      return false
    }
    return true
  }

  const uploadMediaFiles = async (iptvId) => {
    setUploadingMedia(true)
    const uploadPromises = []

    for (const file of pendingFiles) {
      const formData = new FormData()
      formData.append('attachment', file)
      formData.append('iptv', iptvId)
      uploadPromises.push(createIptvMediaFile(formData).unwrap())
    }

    for (const screenshot of pendingScreenshots) {
      const formData = new FormData()
      formData.append('attachment', screenshot)
      formData.append('iptv', iptvId)
      uploadPromises.push(createIptvMediaFile(formData).unwrap())
    }

    try {
      await Promise.all(uploadPromises)

      if (state === 'create') {
        toast.success('IPTV App and all media files uploaded successfully!')
      } else {
        toast.success('IPTV App updated and all media files uploaded successfully!')
      }

      if (state === 'edit' && refetchMediaFiles) {
        refetchMediaFiles()
      }

      setTimeout(() => setOpen(false), 500)
    } catch (error) {
      toast.error('Failed to upload some media files')
      console.error(error)
    } finally {
      setUploadingMedia(false)
      setPendingFiles([])
      setPendingScreenshots([])
    }
  }

  const customFileUpload = ({ file, onSuccess }) => {
    if (validateFile(file)) {
      setPendingFiles(prev => [...prev, file])
      onSuccess('ok')
    }
    return false
  }

  const customScreenshotUpload = ({ file, onSuccess }) => {
    if (validateScreenshot(file)) {
      setPendingScreenshots(prev => [...prev, file])
      onSuccess('ok')
    }
    return false
  }

  const customAppLogoUpload = ({ file, onSuccess }) => {
    if (validateScreenshot(file)) {
      setPendingAppLogo(file)
      setAppLogoFileList([
        {
          uid: file.uid || '-1',
          name: file.name,
          status: 'done',
          url: URL.createObjectURL(file)
        }
      ])
      onSuccess('ok')
    }
    return false
  }

  const handleAppLogoRemove = () => {
    setPendingAppLogo(null)
    setAppLogoFileList([])
    return true
  }

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      app_name: record?.app_name || '',
      comment: record?.comment || '',
      developer: record?.developer || '',
      subscription_type:
        record?.subscription_username && record?.subscription_password
          ? 'username'
          : record?.subscription_code
          ? 'code'
          : 'free',
      subscription_code: record?.subscription_code || '',
      subscription_expiry: record?.subscription_expiry || '',
      subscription_password: record?.subscription_password || '',
      subscription_username: record?.subscription_username || '',
      version: record?.version || '',
      last_updated_by: getUserId()
    },

    validationSchema: Yup.object({
      app_name: Yup.string().required('Please Enter App Name'),
      comment: Yup.string().nullable(),
      developer: Yup.string().nullable(),
      subscription_type: Yup.string().required(
        'Please Choose Subscription Type'
      ),
      subscription_code: Yup.string().nullable(),
      subscription_expiry: Yup.date().nullable(),
      subscription_password: Yup.string().nullable(),
      subscription_username: Yup.string().nullable(),
      version: Yup.mixed().nullable(),
    }),

    onSubmit: async values => {
      if (values.subscription_type === 'free') {
        values.subscription_username = null
        values.subscription_password = null
        values.subscription_code = null
        delete values.subscription_expiry
      } else if (values.subscription_type === 'username') {
        values.subscription_code = null
      } else if (values.subscription_type === 'code') {
        values.subscription_username = null
        values.subscription_password = null
      }
      delete values.subscription_type

      const formData = new FormData()
      for (let key in values) {
        if (values[key] !== null && values[key] !== undefined && values[key] !== '') {
          formData.append(key, values[key])
        }
      }

      if (pendingAppLogo) {
        formData.append('app_logo_path', pendingAppLogo)
      }

      try {
        let iptvId = record?.id
        const hasMediaToUpload = pendingFiles.length > 0 || pendingScreenshots.length > 0

        if (state === 'edit') {
          await updateService({
            body: formData,
            id: record?.id
          }).unwrap()

          if (hasMediaToUpload && iptvId) {
            await uploadMediaFiles(iptvId)
          }
        } else if (state === 'create') {
          const response = await addService(formData).unwrap()
          iptvId = response?.data?.id 

          

          if (hasMediaToUpload && iptvId) {
            await uploadMediaFiles(iptvId)
          }
        }
      } catch (err) {
        console.error(err)
      }
    }
  })

  useEffect(() => {
    if (errorAdd?.data) {
      const errorMessages = Object.values(errorAdd.data).flat()
      errorMessages.forEach(message => {
        toast.error(message)
      })
    } else if (errorUpdate?.data) {
      const errorMessages = Object.values(errorUpdate.data).flat()
      errorMessages.forEach(message => {
        toast.error(message)
      })
    }
  }, [errorAdd, errorUpdate])

  useEffect(() => {
    const hasMediaToUpload = pendingFiles.length > 0 || pendingScreenshots.length > 0

    if (!uploadingMedia && !hasMediaToUpload) {
      if (SuccessAdd && state === 'create') {
        toast.success('IPTV App created successfully!')
        setOpen(false)
      } else if (SuccessUpdate && state === 'edit') {
        toast.success('IPTV App updated successfully!')
        setOpen(false)
      }
    }
  }, [state, SuccessAdd, SuccessUpdate, uploadingMedia, pendingFiles, pendingScreenshots, setOpen])

  useEffect(() => {
    if (!open) {
      setPendingFiles([])
      setPendingScreenshots([])
      setPendingAppLogo(null)
      setAppLogoFileList([])
    }
  }, [open])

  return (
    <Modal
      className='customModal'
      open={open}
      title={state === 'edit' ? `Edit IPTV App` : 'Create IPTV App'}
      onCancel={() => setOpen(false)}
      footer={[
        <Button key='back' onClick={() => setOpen(false)}>
          Cancel
        </Button>,
        <Button
          key='submit'
          type='primary'
          onClick={() => {
            if (Object.keys(validation.errors).length !== 0) {
              Object.keys(validation.errors).map(key => {
                toast.error(validation.errors[key])
              })
              return
            }
            validation.handleSubmit()
          }}
        >
          Submit
        </Button>
      ]}
    >
      <Loader isLoading={isAdding || isUpdating || uploadingMedia} />

      <div className=' mb-3'>
        <label className="form-label">IPTV APP Name</label>
        <Input
          type='text'
          name='app_name'
          onChange={validation.handleChange}
          onBlur={validation.handleBlur}
          value={validation.values.app_name}
        />
        {validation.touched.app_name && validation.errors.app_name ? (
          <p className='text-danger'>{validation.errors.app_name}</p>
        ) : null}
      </div>
      <div className=' mb-3'>
        <label className="form-label">App Developer</label>
        <Input
          type='text'
          name='developer'
          onChange={validation.handleChange}
          onBlur={validation.handleBlur}
          value={validation.values.developer}
        />
        {validation.touched.developer && validation.errors.developer ? (
          <p className='text-danger'>{validation.errors.developer}</p>
        ) : null}
      </div>
      <div className=' mb-3'>
        <label className="form-label">App Version</label>
        <Input
          type='text'
          name='version'
          onChange={validation.handleChange}
          onBlur={validation.handleBlur}
          value={validation.values.version}
        />
        {validation.touched.version && validation.errors.version ? (
          <p className='text-danger'>{validation.errors.version}</p>
        ) : null}
      </div>
      <div className=' mb-3 flex-1'>
        <label className="form-label">Subscription Type</label>
        <br />
        <Select
          className={''}
          style={{
            width: '100%'
          }}
          onChange={e => {
            validation.setFieldValue('subscription_type', e)
          }}
          onBlur={validation.handleBlur}
          value={validation.values?.subscription_type}
          options={[
            { label: 'Username & Password', value: 'username' },
            { label: 'Active Code', value: 'code' },
            { label: 'Free', value: 'free' }
          ]}
        />
        {validation.touched.subscription_type &&
        validation.errors.subscription_type ? (
          <p className='text-danger'>{validation.errors.subscription_type}</p>
        ) : null}
      </div>
      {validation.values.subscription_type == 'username' ? (
        <>
          <div className=' mb-3'>
            <label className="form-label">Subscription Username</label>
            <Input
              type='text'
              name='subscription_username'
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.subscription_username}
            />
            {validation.touched.subscription_username &&
            validation.errors.subscription_username ? (
              <p className='text-danger'>
                {validation.errors.subscription_username}
              </p>
            ) : null}
          </div>
          <div className=' mb-3'>
            <label className="form-label">Subscription Password</label>
            <Input
              type='text'
              name='subscription_password'
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.subscription_password}
            />
            {validation.touched.subscription_password &&
            validation.errors.subscription_password ? (
              <p className='text-danger'>
                {validation.errors.subscription_password}
              </p>
            ) : null}
          </div>
        </>
      ) : null}
      {validation.values.subscription_type == 'username' ||
      validation.values.subscription_type == 'code' ? (
        <div className=' mb-3'>
          <label className="form-label">Subscription Expiry Date</label>
          <br />
          <DatePicker
            style={{ width: '100%' }}
            name='subscription_expiry'
            onChange={(e, date) =>
              validation.setFieldValue('subscription_expiry', date)
            }
            onBlur={validation.handleBlur}
            value={
              validation.values.subscription_expiry
                ? dayjs(validation.values.subscription_expiry)
                : ''
            }
          />
          {validation.touched.subscription_expiry &&
          validation.errors.subscription_expiry ? (
            <p className='text-danger'>
              {validation.errors.subscription_expiry}
            </p>
          ) : null}
        </div>
      ) : null}
      {validation.values.subscription_type == 'code' ? (
        <div className=' mb-3'>
          <label className="form-label">Active Code</label>
          <Input
            type='text'
            name='subscription_code'
            onChange={validation.handleChange}
            onBlur={validation.handleBlur}
            value={validation.values.subscription_code}
          />
          {validation.touched.subscription_code &&
          validation.errors.subscription_code ? (
            <p className='text-danger'>{validation.errors.subscription_code}</p>
          ) : null}
        </div>
      ) : null}
      <div className=' mb-3'>
        <label className="form-label">Comment</label>
        <TextArea
          type='text'
          name='comment'
          onChange={validation.handleChange}
          onBlur={validation.handleBlur}
          value={validation.values.comment}
          rows={5}
        />
        {validation.touched.comment && validation.errors.comment ? (
          <p className='text-danger'>{validation.errors.comment}</p>
        ) : null}
      </div>

      <div className=' mb-3'>
        <label className="form-label">App Logo</label>
        <br />
        {state === 'edit' && record?.app_logo_path && !pendingAppLogo && (
          <div style={{ marginBottom: '10px' }}>
            <img
              src={record.app_logo_path}
              alt="Current app logo"
              style={{
                width: '100px',
                height: '100px',
                objectFit: 'cover',
                borderRadius: '4px',
                border: '2px solid #ddd',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            />
          </div>
        )}
        <Upload
          customRequest={customAppLogoUpload}
          fileList={appLogoFileList}
          onRemove={handleAppLogoRemove}
          maxCount={1}
          showUploadList={true}
          disabled={uploadingMedia || isAdding || isUpdating}
          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/bmp"
          listType="picture"
          beforeUpload={(file) => validateScreenshot(file)}
        >
          <Button icon={<UploadOutlined />} disabled={uploadingMedia || isAdding || isUpdating}>
            {uploadingMedia ? 'Uploading...' : 'Select File'}
          </Button>
        </Upload>
        <p className='text-muted' style={{ fontSize: '12px', marginTop: '5px' }}>
          Only image files are allowed (JPEG, PNG, GIF, WebP, BMP)
        </p>
      </div>

      {state === 'edit' && existingMediaFiles?.data?.results?.length > 0 && (
        <div className=' mb-3'>
          <label className="form-label">Existing Media Files</label>
          <div style={{ padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '4px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {existingMediaFiles.data.results.map((file) => {
              const format = file.file_format?.toLowerCase()
              const isImage = format === 'jpg' || format === 'jpeg' || format === 'png' ||
                             format === 'gif' || format === 'webp' || format === 'bmp'

              return (
                <div key={file.id} style={{ position: 'relative' }}>
                  <a href={file.attachment} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                    {isImage ? (
                      <img
                        src={file.attachment}
                        alt="Media file"
                        style={{
                          width: '100px',
                          height: '100px',
                          objectFit: 'cover',
                          borderRadius: '4px',
                          border: '2px solid #ddd',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: '100px',
                          height: '100px',
                          borderRadius: '4px',
                          border: '2px solid #ddd',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#fff',
                          padding: '5px',
                          textAlign: 'center'
                        }}
                      >
                        <div style={{ fontSize: '30px', marginBottom: '5px', color: '#6c757d' }}>
                          📄
                        </div>
                        <div style={{ fontSize: '9px', fontWeight: 'bold', color: '#495057', marginBottom: '3px', wordBreak: 'break-word', lineHeight: '1.2' }}>
                          {file.attachment?.split('/').pop()?.substring(0, 15) || 'File'}
                          {file.attachment?.split('/').pop()?.length > 15 && '...'}
                        </div>
                        <div style={{ fontSize: '8px', color: '#6c757d', textTransform: 'uppercase' }}>
                          {file.file_format}
                        </div>
                      </div>
                    )}
                  </a>
                  <div style={{ fontSize: '10px', textAlign: 'center', marginTop: '2px', color: '#666' }}>
                    {(file.file_size / 1024).toFixed(1)} KB
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      <div className=' mb-3'>
        <label className="form-label">Upload Files</label>
        <br />
        <Upload
          customRequest={customFileUpload}
          multiple={false}
          showUploadList={true}
          disabled={uploadingMedia || isAdding || isUpdating}
          accept="*"
          beforeUpload={(file) => {
            return validateFile(file)
          }}
        >
          <Button icon={<UploadOutlined />} disabled={uploadingMedia || isAdding || isUpdating}>
            {uploadingMedia ? 'Uploading...' : 'Select File'}
          </Button>
        </Upload>
        <p className='text-muted' style={{ fontSize: '12px', marginTop: '5px' }}>
          Executable files (.exe, .sh, .bat, .cmd, .com, .msi) are not allowed
        </p>
        {pendingFiles.length > 0 && (
          <div style={{ marginTop: '10px' }}>
            <strong>Files to Upload:</strong>
            <ul>
              {pendingFiles.map((file, index) => (
                <li key={index}>{file.name}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className=' mb-3'>
        <label className="form-label">Upload Screenshots</label>
        <br />
        <Upload
          customRequest={customScreenshotUpload}
          multiple={false}
          showUploadList={true}
          disabled={uploadingMedia || isAdding || isUpdating}
          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/bmp"
          listType="picture"
          beforeUpload={(file) => {
            return validateScreenshot(file)
          }}
        >
          <Button icon={<UploadOutlined />} disabled={uploadingMedia || isAdding || isUpdating}>
            {uploadingMedia ? 'Uploading...' : 'Select Screenshot'}
          </Button>
        </Upload>
        <p className='text-muted' style={{ fontSize: '12px', marginTop: '5px' }}>
          Only image files are allowed (JPEG, PNG, GIF, WebP, BMP)
        </p>
        {pendingScreenshots.length > 0 && (
          <div style={{ marginTop: '10px' }}>
            <strong>Screenshots to Upload:</strong>
            <ul>
              {pendingScreenshots.map((file, index) => (
                <li key={index}>{file.name}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

    </Modal>
  )
}

export default EditCreateIptv
